import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./client";

// Plan 2026-10-25 Task 5: GLS-Versandregel je Artikel. Lesen mit stock:read, Aendern mit stock:write
// (Recht article_general). Die Regel gilt fuer den Artikel und alles, was sie erbt (Kinder, Gebinde, Sets).

export type GlsKlasse = "gls_uebergroesse_150" | "gls_uebergroesse_gt150";
export type NetzRolle = "netz" | "zubehoer";
export type VersandFormat = "auto" | "kleinpaket" | "paket";
export interface VersandFormatStufe { abMenge: number; format: Exclude<VersandFormat, "auto"> }
export const VERSAND_FORMATE: ReadonlyArray<{ code: VersandFormat; anzeige: string }> = [
  { code: "auto", anzeige: "Automatisch nach Gewicht und Maß" },
  { code: "kleinpaket", anzeige: "Mindestens Warenpost / Kleinpaket" },
  { code: "paket", anzeige: "Paket erforderlich" },
];

export const GLS_KLASSEN: ReadonlyArray<{ code: GlsKlasse; anzeige: string }> = [
  { code: "gls_uebergroesse_150", anzeige: "GLS Übergröße ≤ 150 cm" },
  { code: "gls_uebergroesse_gt150", anzeige: "GLS Übergröße > 150 cm" },
];

export const NETZ_ROLLEN: ReadonlyArray<{ code: NetzRolle; anzeige: string }> = [
  { code: "netz", anzeige: "Netz (für GLS-Profile)" },
  { code: "zubehoer", anzeige: "Zubehör (erzwingt kein GLS)" },
];

export const SCHWELLE_MIN = 1;
export const SCHWELLE_MAX = 1000;
export const NOTIZ_MAX = 500;
/** Ab dieser laengsten Seite schlaegt die Maske GLS vor; gespeichert wird nur von Hand. */
export const VORSCHLAG_AB_MM = 1200;
export const GT150_AB_MM = 1500;

export interface VersandRegelWerte {
  glsAbMenge: number | null;
  glsKlasse: GlsKlasse | null;
  netzRolle: NetzRolle | null;
  mindestFormat?: VersandFormat | null;
  paketAbMenge?: number | null;
  formatStufen?: VersandFormatStufe[] | null;
}

export interface VersandRegelZeile extends VersandRegelWerte {
  stockItemId: number;
  notiz: string | null;
  geaendertVon: string;
  geaendertAm: string;
}

export interface VersandQuelle {
  ownerId: number;
  units: number;
  regel: VersandRegelWerte;
}

export interface VersandRegelAntwort {
  sku: string;
  eigene: VersandRegelZeile | null;
  quellen: VersandQuelle[];
}

export interface VersandRegelBody {
  gls_ab_menge: number | null;
  gls_klasse: GlsKlasse | null;
  netz_rolle: NetzRolle | null;
  notiz: string | null;
  mindest_format?: VersandFormat | null;
  paket_ab_menge?: number | null;
  format_stufen?: VersandFormatStufe[] | null;
}

const pfad = (sku: string) => `/stock/shipping-rules/${encodeURIComponent(sku)}/`;

export function useVersandregel(sku: string | undefined) {
  return useQuery({
    queryKey: ["versandregel", sku],
    queryFn: () => api.get<VersandRegelAntwort>(pfad(sku!)),
    enabled: typeof sku === "string" && sku !== "",
    retry: false,
  });
}

export function useSetVersandregel(sku: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VersandRegelBody) => api.put<{ sku: string; regel: VersandRegelZeile }>(pfad(sku), body),
    // Quellen haengen an der eigenen Regel — neu laden statt lokal nachbauen.
    onSuccess: () => qc.invalidateQueries({ queryKey: ["versandregel", sku] }),
  });
}

export function useLoescheVersandregel(sku: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bereich: "gls" | "format") => api.delete<{ sku: string; geloescht: true }>(pfad(sku) + (bereich === "format" ? "?bereich=format" : "")),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["versandregel", sku] }),
  });
}
